import { useMemo } from "react";
import { MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { AuthOnFileRpaStatus } from "../enums/authOnFileAuthStatus";
import { AuthOnFileRpaErrorType } from "../enums/authOnFileRpaErrorType";
import { DropdownLocation } from "../enums/dropdownLocation";
import { getErrorType } from "../pages/singleOrderLayout/utils/checkRpaError";
import { formatErrorType } from "../pages/singleOrderLayout/utils/errorFormatter";
import { getAllJCodes } from "../pages/singleOrderLayout/utils/existingDocsToLs";

/**
 * Checks if business office rows are missing
 */
const isBusinessOfficeRowsMissing = (authOnFile: any): boolean => {
  return (
    !authOnFile ||
    !authOnFile.reports ||
    (Array.isArray(authOnFile.reports) && authOnFile.reports.length === 0)
  );
};

/**
 * Checks if regimen is missing
 */
const isRegimenMissing = (
  authOnFile: any,
  jCodes: any[] | null | undefined,
): boolean => {
  return !authOnFile || !jCodes || jCodes.filter(Boolean).length === 0;
};

/**
 * Checks if visit date content is missing
 */
const isVisitDateContentMissing = (authOnFile: any): boolean => {
  return (
    !authOnFile ||
    !authOnFile.visit_data_charts ||
    Object.keys(authOnFile.visit_data_charts).length === 0
  );
};

/**
 * Determines if the expected error type is present in the order data
 */
const checkIfErrorTypePresent = (
  expectedErrorType: AuthOnFileRpaErrorType,
  authOnFile: any,
  jCodes: any[] | null | undefined,
): boolean => {
  switch (expectedErrorType) {
    case AuthOnFileRpaErrorType.BUSINESS_OFFICE_ROWS_NOT_FOUND:
      return isBusinessOfficeRowsMissing(authOnFile);
    case AuthOnFileRpaErrorType.REGIMEN_NOT_FOUND:
      return isRegimenMissing(authOnFile, jCodes);
    case AuthOnFileRpaErrorType.VISIT_DATE_CONTENT_NOT_FOUND:
      return isVisitDateContentMissing(authOnFile);
    default:
      return false;
  }
};

/**
 * Handles error state when auth on file has an error status
 */
const handleAuthOnFileError = (errorType: string | undefined) => {
  if (!errorType) {
    return {
      shouldShowError: false,
      errorMessage: "",
      dropdownLocation: DropdownLocation.NONE,
      isCompletedState: false,
    };
  }

  return {
    shouldShowError: true,
    errorMessage: formatErrorType(errorType),
    dropdownLocation: DropdownLocation.BOTH,
    isCompletedState: false,
  };
};

/**
 * Handles error state when auth on file is completed but has validation issues
 */
const handleCompletedStateError = (
  isBusinessOfficePresent: boolean | undefined,
  isCptTablePresent: boolean | undefined,
) => {
  if (!isCptTablePresent) {
    return {
      shouldShowError: true,
      errorMessage: "No valid drug scheduled under this regimen",
      dropdownLocation: DropdownLocation.CPT,
      isCompletedState: true,
    };
  }

  if (!isBusinessOfficePresent) {
    return {
      shouldShowError: true,
      errorMessage: "Drug scheduled but business office not found",
      dropdownLocation: DropdownLocation.BUSINESS_OFFICE,
      isCompletedState: true,
    };
  }

  return {
    shouldShowError: false,
    errorMessage: "",
    dropdownLocation: DropdownLocation.NONE,
    isCompletedState: false,
  };
};

export const useAuthOnFileError = (
  order: MedicalPaOrder | undefined,
  expectedErrorType: AuthOnFileRpaErrorType,
) => {
  const orderData = order ?? {};
  const authOnFile = order?.auth_on_file;
  const errorType = getErrorType(orderData);
  const authOnFileStatus = order?.status?.auth_on_file?.status;
  const isAuthOnFileError = authOnFileStatus === AuthOnFileRpaStatus.ERROR;
  const isAuthOnFileCompleted =
    authOnFileStatus === AuthOnFileRpaStatus.COMPLETED;

  const jCodes = useMemo(() => getAllJCodes(orderData), [orderData]);
  const isCptTablePresent =
    jCodes && Array.isArray(jCodes) && jCodes.filter(Boolean).length > 0;

  const isBusinessOfficePresent =
    authOnFile?.reports &&
    Array.isArray(authOnFile.reports) &&
    authOnFile.reports.length > 0;

  // Handle error state when auth on file has an error
  if (isAuthOnFileError) {
    return handleAuthOnFileError(errorType);
  }

  // Handle completed state with validation errors
  if (isAuthOnFileCompleted) {
    const isErrorTypePresent = checkIfErrorTypePresent(
      expectedErrorType,
      authOnFile,
      jCodes,
    );

    if (isErrorTypePresent) {
      return handleCompletedStateError(
        isBusinessOfficePresent,
        isCptTablePresent,
      );
    }
  }

  // No error to show
  return {
    shouldShowError: false,
    errorMessage: "",
    dropdownLocation: DropdownLocation.NONE,
    isCompletedState: false,
  };
};
